/**
 * The transport every generated client is built on.
 *
 * Deliberately an interface rather than a fetch call. A channel application
 * needs to attach a bearer token, a tenant, a correlation id and an idempotency
 * key to requests it did not write, and a test needs to answer them without a
 * network. Both are the same problem — what sits between the typed operation
 * and the wire — and both are solved by making that seam explicit.
 *
 * Nothing here knows about any service. The generated modules supply the paths,
 * the shapes and the scopes; this supplies the plumbing and the error contract.
 */
/**
 * A refusal carrying what the service actually said.
 *
 * The category is the part worth branching on. A status alone cannot separate
 * "this will never succeed" from "try again in a moment", which is exactly the
 * confusion that had clients retrying a quote id that did not exist; the
 * category says which, in the service's own words.
 */
export class ApiError extends Error {
    status;
    code;
    category;
    details;
    constructor(status, code, category, message, details) {
        super(message);
        this.status = status;
        this.code = code;
        this.category = category;
        this.details = details;
        this.name = "ApiError";
    }
    /** Whether making the identical request again could plausibly succeed. */
    get retryable() {
        return this.category === "dependency" || this.category === "rate_limit" || this.status >= 500;
    }
}
/** A failure that never reached a service: DNS, socket, CORS, abort. */
export class TransportError extends Error {
    cause;
    constructor(message, cause) {
        super(message);
        this.cause = cause;
        this.name = "TransportError";
    }
}
export function createHttpTransport(options) {
    const call = options.fetch ?? globalThis.fetch.bind(globalThis);
    return {
        async request(input) {
            const url = new URL(input.path.replace(/^\//, ""), ensureTrailingSlash(options.baseUrl));
            for (const [key, value] of Object.entries(input.query ?? {})) {
                if (value !== undefined)
                    url.searchParams.set(key, value);
            }
            const headers = { accept: "application/json" };
            if (input.body !== undefined)
                headers["content-type"] = "application/json";
            const token = await options.authorization?.();
            if (token !== undefined)
                headers.authorization = `Bearer ${token}`;
            const tenantId = options.tenantId?.();
            if (tenantId !== undefined)
                headers["x-tenant-id"] = tenantId;
            if (input.options?.idempotencyKey !== undefined) {
                headers["idempotency-key"] = input.options.idempotencyKey;
            }
            if (input.options?.correlationId !== undefined) {
                headers["x-correlation-id"] = input.options.correlationId;
            }
            Object.assign(headers, input.options?.headers ?? {});
            let response;
            try {
                response = await call(url, {
                    method: input.method,
                    headers,
                    ...(input.body === undefined ? {} : { body: JSON.stringify(input.body) }),
                    ...(input.options?.signal === undefined ? {} : { signal: input.options.signal }),
                });
            }
            catch (cause) {
                // Never reached a service, so there is no category to read and none to
                // invent. A caller that needs to distinguish this from a refusal can,
                // because it is a different class.
                throw new TransportError("The request did not reach the service", cause);
            }
            const text = await response.text();
            /*
             * Parsed inside a try, and after the status is known to matter rather than before it.
             *
             * `asApiError` below is written for a body that is not the platform's error envelope — its
             * own comment says a gateway or proxy can answer with HTML or nothing at all — but it never
             * used to get the chance, because this line parsed the body unconditionally and a
             * `SyntaxError` escaped the transport entirely. A proxy's HTML error page therefore reached
             * the screen as `Unexpected token '<'`, which names the client's parser instead of the
             * service that refused, and sends somebody looking for a front-end bug.
             *
             * Measured on 19 October: every absent service behind the two mobile applications reported
             * itself this way. nginx answers 502 with an HTML page, and 502 is the one thing the reader
             * needed to be told.
             */
            let payload;
            let isJson = text.length === 0;
            if (text.length > 0) {
                try {
                    payload = JSON.parse(text);
                    isJson = true;
                }
                catch {
                    // Left for the two branches below, which differ on whether the status already explains it.
                }
            }
            if (!response.ok)
                throw asApiError(response.status, isJson ? payload : undefined);
            /*
             * A successful status carrying something that is not JSON is its own failure, and a
             * different one: nothing refused, so there is no status to report and no envelope to read.
             * It is what a proxy serving an index.html for an unrouted path looks like.
             */
            if (!isJson) {
                throw new TransportError(`The service answered ${String(response.status)} with a body that is not JSON`);
            }
            return payload;
        },
    };
}
function ensureTrailingSlash(value) {
    return value.endsWith("/") ? value : `${value}/`;
}
/**
 * Read the service's own error envelope, and do not pretend when it is absent.
 *
 * A gateway or proxy can answer with HTML or nothing at all, and a client that
 * reported that as a platform error code would have an operator searching the
 * services for a code no service ever emitted.
 */
function asApiError(status, payload) {
    const envelope = payload;
    const error = envelope?.error;
    if (error && typeof error.code === "string" && typeof error.category === "string") {
        return new ApiError(status, error.code, error.category, error.message ?? "", error.details);
    }
    return new ApiError(status, "unrecognised_error_response", status >= 500 ? "internal" : "validation", `The service answered ${status} without the platform error envelope`);
}
//# sourceMappingURL=runtime.js.map